import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useCart } from "@/contexts/CartContext";
import { formatINR, imgUrl } from "@/lib/api";
import { Minus, Plus, Trash2 } from "lucide-react";

export default function Cart() {
  const { items, updateQty, remove, subtotal } = useCart();
  const navigate = useNavigate();
  const shipping = subtotal >= 2000 ? 0 : 99;
  const tax = Math.round(subtotal * 0.05);
  const total = subtotal + shipping + tax;

  return (
    <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12" data-testid="cart-page">
      <h1 className="font-serif text-4xl sm:text-5xl text-[#2b1b1c] mb-2">Your Bag</h1>
      <p className="text-[#7c7273] mb-10">Review your treasures before checkout.</p>

      {items.length === 0 ? (
        <div className="text-center py-24 bg-[#f5f3ef]">
          <p className="font-italic-serif text-2xl text-[#7c7273]">Your bag is empty.</p>
          <Link to="/shop" className="btn-primary inline-block mt-6" data-testid="empty-cart-shop">Discover Sarees</Link>
        </div>
      ) : (
        <div className="grid lg:grid-cols-[1fr_360px] gap-10">
          <div className="divide-y divide-[#e8e4db] border-y border-[#e8e4db]">
            {items.map((it) => (
              <div key={`${it.product_id}::${it.variant_id || "base"}`} className="py-6 flex gap-4" data-testid={`cart-row-${it.product_id}`}>
                <img src={imgUrl(it.image)} alt={it.name} className="w-24 h-32 object-cover bg-[#f5f3ef]" />
                <div className="flex-1">
                  <Link to={`/product/${it.product_id}`} className="font-serif text-xl text-[#2b1b1c] hover:text-[#7a1e2b]">{it.name}</Link>
                  {it.color && <div className="text-xs text-[#7c7273] mt-0.5 uppercase tracking-widest">Colour · {it.color}</div>}
                  <div className="text-[#7a1e2b] mt-1">{formatINR(it.price)}</div>
                  <div className="mt-3 flex items-center gap-3">
                    <div className="flex items-center border border-[#e8e4db]">
                      <button onClick={() => updateQty(it.product_id, it.quantity - 1, it.variant_id)} className="p-1.5 hover:bg-[#f5f3ef]"><Minus size={12} /></button>
                      <span className="px-3">{it.quantity}</span>
                      <button onClick={() => updateQty(it.product_id, it.quantity + 1, it.variant_id)} className="p-1.5 hover:bg-[#f5f3ef]"><Plus size={12} /></button>
                    </div>
                    <button onClick={() => remove(it.product_id, it.variant_id)} className="text-[#7c7273] hover:text-[#7a1e2b] flex items-center gap-1 text-sm" data-testid={`cart-remove-${it.product_id}`}>
                      <Trash2 size={14} /> Remove
                    </button>
                  </div>
                </div>
                <div className="font-serif text-lg text-[#2b1b1c]">{formatINR(it.price * it.quantity)}</div>
              </div>
            ))}
          </div>

          <aside className="bg-[#f5f3ef] p-8 lg:sticky lg:top-28 lg:self-start" data-testid="cart-summary">
            <h3 className="font-serif text-xl text-[#2b1b1c] mb-4">Order Summary</h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-[#7c7273]">Subtotal</span><span>{formatINR(subtotal)}</span></div>
              <div className="flex justify-between"><span className="text-[#7c7273]">Shipping</span><span>{shipping === 0 ? "Free" : formatINR(shipping)}</span></div>
              <div className="flex justify-between"><span className="text-[#7c7273]">Tax (5%)</span><span>{formatINR(tax)}</span></div>
              <div className="h-px bg-[#e8e4db] my-2" />
              <div className="flex justify-between text-lg font-serif"><span>Total</span><span className="text-[#7a1e2b]">{formatINR(total)}</span></div>
            </div>
            <button className="btn-primary w-full mt-6" onClick={() => navigate("/checkout")} data-testid="cart-proceed-checkout">
              Proceed to Checkout
            </button>
            <p className="text-xs text-[#7c7273] mt-4 text-center">Secure checkout · Razorpay & COD</p>
          </aside>
        </div>
      )}
    </div>
  );
}
